import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';

interface ReusableAutocompleteProps<T> {
  options: T[];
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => string | number;
  onChange: (selectedValue: T | null) => void;
  onChangeAgent: (searchTerm: string) => void;
  placeholder?: string;
  className?: string;
  value?: string | number;
  isDisabled?: boolean;
}

function ReusableAutocomplete<T>({
  options,
  getOptionLabel,
  getOptionValue,
  onChange,
  placeholder = 'Search...',
  className,
  value,
  isDisabled = false,
}: ReusableAutocompleteProps<T>) {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);
  }, []);

  const handleClear = useCallback(() => {
    setSearchTerm('');
    onChange(null); // Clear the selected filter
  }, [onChange]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) {
      return options;
    }
    return options.filter((option) =>
      getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, options, getOptionLabel]);

  const handleSelect = useCallback(
    (option: T) => {
      setSearchTerm(getOptionLabel(option));
      setShowDropdown(false);
      onChange(option);
    },
    [onChange, getOptionLabel],
  );

  const handleFocus = useCallback(() => {
    setShowDropdown(true);
  }, []);

  return (
    <div ref={wrapperRef} className={`relative ${className}  `}>
      <div className="relative">
        <input
          type="text"
          className="w-full px-3 py-2 border border-customBorder-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500  text-textColorGray bg-backgroundColor-white"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleChange}
          disabled={isDisabled}
          onFocus={handleFocus}
        />
        {searchTerm && (
          <button
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 cursor-pointer"
            onClick={handleClear}
          >
            ✖
          </button>
        )}
      </div>

      {showDropdown && filteredOptions.length > 0 && (
        <ul className="absolute w-full  bg-sidebar-background  text-textColorGray  border rounded-md shadow-md mt-1 max-h-40 overflow-y-auto">
          {filteredOptions.map((option) => (
            <li
              key={getOptionValue(option)}
              className="px-3 py-2 cursor-pointer hover:bg-blue-100"
              onClick={() => handleSelect(option)}
            >
              {getOptionLabel(option)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ReusableAutocomplete;
